import { useEffect, useRef, useState } from 'react'
import type { FormEvent, JSX } from 'react'
import {
  RUN_COMMANDS,
  chartInstallerMatch,
  commandSuitsStatus,
  installerMatchMessage,
  installerTag,
  isCustomerRole,
  isRole,
  missingRequiredVariables,
  deploymentIdProblem,
  refusedVariableProblem,
  regionProblem,
  roleHasCapability,
  searchBackendProblem,
} from '@qim/shared'
import { explainFailure, startRun } from '../api.js'
import { ConfirmDestroy } from './confirm-destroy.jsx'
import { approvalNotice, useApprovalFacts } from './approvals.jsx'
import { LifetimePicker, lifetimeFor as lifetimeRequestFor } from './lifetime.jsx'
import { Preflight, isBlockingFailure, shownChecks } from './preflight.jsx'
import {
  ES_DEFAULT,
  OPENSEARCH_DEFAULT,
  displayValue,
  extraVariableNames,
  forAudience,
  initialDocument,
  isJsonObject,
  parseValue,
  readDocumentText,
  searchBackendOf,
  stagingInstanceDefault,
  typeLabel,
  writeDocumentText,
} from './run-document.js'
import type {
  Capability,
  JsonValue,
  PreflightResult,
  ProtectionStanding,
  RunCommand,
} from '@qim/shared'
import type { RunAudience, SearchBackendChoice } from './run-document.js'
import type { RunSummary } from '../api.js'

/**
 * Which protection a destroy from this form would meet, read off the instance
 * as the API labelled it for this viewer — the form decides nothing (IM-189).
 *
 * The second case is why this exists: an instance whose protection *you*
 * removed shows no lock, and the form used to promise your destroy would go
 * straight through while the API held it for a second approver.
 */
const protectionOf = (
  existing:
    | {
        readonly protected?: unknown
        readonly protectionRemovedByYou?: { readonly removedAt: string; readonly until: string }
      }
    | undefined,
): ProtectionStanding =>
  existing?.protected !== undefined
    ? { kind: 'standing' }
    : existing?.protectionRemovedByYou !== undefined
      ? { kind: 'removed-by-requester', ...existing.protectionRemovedByYou }
      : { kind: 'none' }

/**
 * Starting a run.
 *
 * The console's only write action, so it is also the only place where a mistake
 * here reaches a customer's infrastructure. Four things follow from that.
 *
 * **The configuration is one document, and the form is a projection over it**
 * (IM-126). The eleven curated fields read out of `doc` and write back into it;
 * every other key the document carries gets a name/value row beside them, and
 * the same document can be edited as JSON. Not two states kept in step — that
 * arrangement drops a variable the form cannot express the moment someone
 * switches back to the form and submits, which is precisely what the old
 * `formValuesFrom` did to a remembered document with extra keys in it.
 *
 * **The commands offered follow the caller's capabilities.** Not because it is a
 * control — the API checks every route and would refuse anyway — but because
 * offering someone a button that always fails is a worse way to tell them they
 * cannot do something than not offering it.
 *
 * **Nothing is filled in for them silently.** A deployment id typed by hand is a
 * deliberate act; one defaulted from the last thing viewed is how a plan gets
 * run against the wrong instance.
 *
 * **A refusal is shown with its reason.** The API returns the run record with a
 * failure on it, or a `detail` when it refused before any run existed, and
 * "grant-expired" plus what to do about it is the difference between a support
 * engineer fixing it in a minute and filing a ticket.
 */

/**
 * What each command is, and what holding it requires.
 *
 * Keyed by `RunCommand` and rendered in `RUN_COMMANDS` order, so a command added
 * to the contract stops compiling here until someone decides whether the console
 * offers it. This used to restate the union as `'plan' | 'apply' | 'destroy'`,
 * which is exactly how `output` reached the route table, the API client and the
 * run pipeline in IM-40 and still never reached a button — leaving the endpoints
 * screen with no way to get endpoints newer than the last apply, which is the
 * one job IM-40 gives an `output` run.
 *
 * `tone` is how dangerous the command is, and it is on the record rather than at
 * the button so that a command added to the contract has to answer the question.
 * Destroy and Plan were pixel-identical until IM-73: one shows what would change
 * and the other tears down a customer's instance.
 *
 * `output` carries `outputs:refresh`, which is held by everyone who diagnoses —
 * including `support-engineer`, who cannot plan or apply. It deliberately does
 * *not* carry `deployment:read`: that is the floor every role builds on, so it
 * would have put a control that dispatches into a customer's account in front of
 * `sales-cs`. See IM-75, fixed alongside this.
 */
const COMMAND_DETAIL: Record<
  RunCommand,
  {
    readonly capability: Capability
    readonly label: string
    readonly hint: string
    /** '' is the primary style; see the note above. */
    readonly tone: 'ghost' | '' | 'danger'
    /**
     * What the instance must be for this command to be offered, in words, when
     * that is narrower than "anything". `commandSuitsStatus` is the rule; this
     * is how the form says why a button is greyed out.
     */
    readonly needs?: string
  }
> = {
  plan: {
    capability: 'plan:run',
    label: 'Plan',
    hint: 'Shows what would change. Changes nothing.',
    tone: 'ghost',
  },
  apply: {
    capability: 'deployment:create',
    label: 'Apply',
    hint: 'Creates or updates the instance. Takes 30–90 minutes.',
    tone: '',
  },
  output: {
    capability: 'outputs:refresh',
    label: 'Refresh endpoints',
    hint: 'Re-reads the endpoints this instance serves on. Changes nothing.',
    tone: 'ghost',
    needs: 'a live instance',
  },
  destroy: {
    capability: 'deployment:destroy',
    label: 'Destroy',
    hint: 'Tears the instance down.',
    tone: 'danger',
    needs: 'an instance that is live or whose last run failed',
  },
}

const COMMANDS: readonly {
  readonly command: RunCommand
  readonly capability: Capability
  readonly label: string
  readonly hint: string
  readonly tone: 'ghost' | '' | 'danger'
  readonly needs?: string
}[] = RUN_COMMANDS.map((command) => ({ command, ...COMMAND_DETAIL[command] }))

/**
 * What an apply is called when it changes the version (IM-107).
 *
 * The same command, the same route and the same capability — only the word, and
 * the word is the point: an upgrade is an apply whose chart version differs, and
 * a button that says "Apply" for both leaves the field that distinguishes them
 * looking optional.
 */
const UPGRADE_HINT =
  'Applies this instance at a new chart version. Takes 30–90 minutes, and it is the same run as an apply.'

export interface StartRunProps {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly role: string
  /**
   * Which console this is — `AppConfig.environment`, from `/config.json`.
   *
   * Only to default the Qrvey-internal box (IM-175): on in dev and staging,
   * off in production and anywhere unrecognised. Required, so a screen that
   * forgot to pass it fails to compile rather than quietly starting every
   * test instance with the customer's answer.
   */
  readonly environment: string
  /** Prefilled when re-running against a deployment that already exists. */
  readonly deploymentId?: string
  /**
   * The regions this account is set up in — the choices for a *new* deployment.
   *
   * Only these, because a region with no state bucket has nowhere to keep a
   * deployment's state: the run reaches the customer's account and dies there.
   * `regionProblem` deliberately validates shape and not membership, and says
   * so — this list is the half of that guard that rejects `us-esat-1`.
   *
   * Undefined means nobody has looked, and an empty list means the account has
   * none. Both fall back to typing one rather than blocking the form: a failed
   * lookup is usually ours, and someone who knows the region should not be
   * stopped by it.
   */
  readonly regions?: readonly string[]
  /**
   * The deployments that already exist, so an id that names one is fixed.
   *
   * Keyed across the whole customer and not per account, because that is what
   * the API does — `regionOfDeployment` reads the tenant's runs. Filtering by
   * account here would let the form offer a choice the API then overrules.
   */
  readonly deployments?: readonly {
    readonly deploymentId: string
    readonly region?: string
    /**
     * As the API derived it, so Destroy and Refresh endpoints are offered only
     * where they mean something — see `commandSuitsStatus`. Required, because
     * a caller that forgot it would silently grey both out.
     */
    readonly status: string
    /** What it last ran with, secrets already removed. See `remembered`. */
    readonly variables?: Readonly<Record<string, unknown>>
    /** Protected, so a destroy needs a second approver (IM-39). */
    readonly protected?: { readonly by: string }
    /** You lifted its protection lately, so your destroy still waits (IM-189). */
    readonly protectionRemovedByYou?: { readonly removedAt: string; readonly until: string }
  }[]
  /**
   * The chart version running on this deployment now, when it has one.
   *
   * Only so an apply that changes it can be *called* an upgrade (IM-107). An
   * upgrade is an apply with a different `qrvey_chart_version` and nothing else
   * — there is no second command and no second route — but spelling it "Apply"
   * hides the one field that decides what the run does, and someone reading a
   * button marked Apply has no reason to look at a text box further up.
   *
   * Deliberately **not** used to prefill the version field. Carrying the
   * deployed version into the box would make the default a re-apply of what is
   * already there: a run nobody wants and the easiest one to start by accident.
   * It is shown beside the field instead, so the comparison is on screen and
   * the typing stays deliberate.
   */
  readonly deployedVersion?: string
  /**
   * The regions whose dispatcher hands the runner its registry credentials.
   *
   * Per region because the secret lives in the dispatcher stack, which is
   * deployed once per region — an account can genuinely supply it in one and
   * not another, and `RegionAssessment.suppliesRegistryKey` answers it there.
   *
   * Undefined means nobody has looked, which is treated as "ask for a key": the
   * cost of asking unnecessarily is one paste, and the cost of not asking when
   * it was needed is a run that dies ninety seconds into a container.
   */
  readonly registryFromAccount?: readonly string[]
  /**
   * The installer image each region's dispatcher runs, by region (IM-196).
   *
   * The release pipeline builds the installer and the chart from one version,
   * so the form shows the installer the run will use beside the chart field,
   * offers its version in one click, and says when the typed chart is from
   * another release. Compared here on the keystroke, with the same function
   * the preflight uses, because the preflight is asked again only when the
   * region or the instance changes. Undefined means nobody has looked, and the
   * form says nothing rather than guessing.
   */
  readonly installerImages?: Readonly<Record<string, string>>
  /**
   * Whether the person filling this in is Qrvey staff (IM-102). A customer's
   * own run never waits for approval; a staff run in their tenant may. Absent
   * is staff, which is who uses the console until customer sign-in (IM-154).
   */
  readonly requesterIsStaff?: boolean
  /**
   * Asks the API whether a run could succeed before it is started (IM-36).
   *
   * Passed by the screen rather than called here, for the same reason the
   * account's regions are: it reads into the customer's account, and whether a
   * panel may do that is the caller's decision — the one that knows the panel
   * has actually been opened. Absent means the form checks only what it can
   * compute itself.
   */
  readonly preflight?: (
    command: RunCommand,
    variables: Readonly<Record<string, unknown>>,
    region?: string,
  ) => Promise<PreflightResult>
  readonly onStarted: (run: RunSummary) => void
  /**
   * Navigates in place, so an account check the preflight refuses on can point
   * at the account's onboarding screen (IM-181) — which is where each of those
   * is fixed. Absent offers the fix in words only.
   */
  readonly onOpen?: (path: string) => void
}

/**
 * How long the form waits after a change before asking again (IM-36).
 *
 * Only a change to the region or to which instance is named asks again — see
 * `preflightKey` — so this is for the region box being typed into, not for
 * every keystroke in the document.
 */
export const PREFLIGHT_DEBOUNCE_MS = 400

/**
 * Why the buttons are disabled, in one or two sentences (IM-36).
 *
 * Split by basis, because the two hold different buttons: an account check
 * holds every command and a run check only the one the preflight asked about —
 * and "Plan cannot start until the account block is lifted" would be false.
 */
const preflightReason = (
  blocking: readonly { readonly basis: string; readonly title: string }[],
  askedLabel: string | undefined,
): string => {
  const titles = (checks: readonly { readonly title: string }[]): string =>
    `${checks.map((c) => c.title).join(', ')} ${checks.length === 1 ? 'is' : 'are'} fixed`
  const account = blocking.filter((c) => c.basis === 'account')
  const run = blocking.filter((c) => c.basis !== 'account')
  return [
    account.length === 0 ? undefined : `No run can start until ${titles(account)}.`,
    run.length === 0 ? undefined : `${askedLabel ?? 'This run'} cannot start until ${titles(run)}.`,
    'What to do is listed above.',
  ]
    .filter((part) => part !== undefined)
    .join(' ')
}

export const StartRun = ({
  baseUrl,
  tenantId,
  accountKey,
  role,
  environment,
  deploymentId,
  regions,
  deployments,
  deployedVersion,
  registryFromAccount,
  installerImages,
  requesterIsStaff = true,
  preflight,
  onStarted,
  onOpen,
}: StartRunProps): JSX.Element => {
  /** The tenant's approval rule, so the form can say what will wait (IM-102). */
  const approvalFacts = useApprovalFacts(baseUrl, tenantId)
  /**
   * Who the document is being written for (IM-175).
   *
   * A customer never sees the Qrvey-internal box and their document never
   * carries `staging_instance` — see `STAFF_ONLY_VARIABLES`. Decided from the
   * role through `isCustomerRole` rather than from `requesterIsStaff`, which
   * defaults to staff when a screen does not say: the wrong default here would
   * show a customer a switch to Qrvey's dev registry.
   */
  const audience: RunAudience = { environment, customer: isCustomerRole(role) }
  /**
   * The run's whole configuration, and the only state the form writes to.
   *
   * Every control below is a projection: it reads a key out of here and writes
   * one back. A key this form has no control for is rendered as a name/value
   * row rather than dropped, which is the difference between this and the
   * sixteen form fields it replaces.
   */
  const [doc, setDoc] = useState<Record<string, JsonValue>>(() =>
    initialDocument(deploymentId, tenantId, audience),
  )
  /** Which projection is on screen. The document is the same either way. */
  const [view, setView] = useState<'form' | 'json'>('form')
  /**
   * The JSON editor's text while it is being edited, and `undefined` otherwise.
   *
   * Two states would be the mistake this story is about, so this is deliberately
   * not a second copy of the document: while it is `undefined` the textarea
   * renders the document itself, and every edit that parses is committed
   * straight back to `doc`. It holds text only for as long as the text does not
   * agree with the document — which is exactly the interval where the person is
   * mid-keystroke and there is nothing to commit.
   */
  const [jsonText, setJsonText] = useState<string | undefined>(undefined)
  /**
   * Extra rows being typed into, keyed by variable name.
   *
   * A committed value renders canonically — `displayValue` quotes strings, so
   * the round trip keeps its type — and re-serialising on every keystroke would
   * fight whoever is typing `{"a":` on the way to an object. So the raw text
   * lives here until the field is left, and the parsed value goes into the
   * document immediately.
   */
  const [rowText, setRowText] = useState<Readonly<Record<string, string>>>({})
  /** The name and value of the variable being added, before it is committed. */
  const [newVar, setNewVar] = useState({ name: '', value: '' })
  const [addFault, setAddFault] = useState<string | undefined>(undefined)
  /**
   * How long a *new* instance is meant to live (IM-134).
   *
   * Not part of the variables document: the installer has never heard of it,
   * and it is a fact this control plane records rather than something Terraform
   * acts on. Sent at the top level, like the region, and for the same reason.
   */
  const [lifetime, setLifetimeChoice] = useState('')
  /**
   * Where a *new* deployment goes. Undefined until someone chooses.
   *
   * Left undefined rather than seeded from `regions[0]` in an initialiser,
   * because the list arrives after the form does — the parent looks it up when
   * this panel is opened. A `useState` initialiser runs once, so it would have
   * captured an empty list; an effect that re-seeded it would overwrite a
   * choice already made. Defaulting where it is read costs neither.
   */
  const [region, setRegion] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  /**
   * Which deployment's remembered values are currently in the form.
   *
   * A ref rather than state because it decides *whether to fill*, not what to
   * render: filling sets it, and it is what stops the effect refilling over an
   * edit the person just made. Cleared when they clear the form, so a second
   * fill is available without retyping the id.
   */
  const filledFrom = useRef<string | undefined>(undefined)
  const [filledFor, setFilledFor] = useState<string | undefined>(undefined)
  /**
   * Set while the destroy confirmation is open. See `confirm-destroy.tsx`.
   *
   * Only `destroy` goes through it: gating `apply` as well would make the dialog
   * something people dismiss by habit, which is how a confirmation stops
   * confirming anything.
   */
  const [confirming, setConfirming] = useState(false)
  /** So cancelling returns focus where it came from, not to the top of the form. */
  const destroyButton = useRef<HTMLButtonElement>(null)
  /**
   * Set on cancel, acted on once the dialog has actually gone.
   *
   * Focus cannot be moved out of an open modal dialog — everything outside it is
   * inert — so calling `focus()` in the cancel handler does nothing at all: the
   * dialog is still mounted at that point and the call is silently dropped.
   * Doing it from an effect runs it after React has committed the unmount, when
   * the button is reachable again.
   *
   * jsdom does not implement that inertness, so this is invisible to the tests
   * and was found by cancelling the dialog in a real browser and watching focus
   * land on `<body>`.
   */
  const returnFocus = useRef(false)

  useEffect(() => {
    if (confirming || !returnFocus.current) return
    returnFocus.current = false
    destroyButton.current?.focus()
  }, [confirming])

  /*
   * Reading and writing the document, which is all any control below does.
   *
   * `setVar(name, undefined)` removes the key rather than setting it blank: an
   * empty text box means the variable is not being sent, which is what the
   * sixteen `values.x === '' ? {} : {...}` spreads used to say at send time.
   * The cost is that clearing and retyping a field moves it to the end of the
   * document — key order is insertion order — which the JSON view makes visible
   * and nobody has a reason to mind.
   */
  const setVar = (name: string, value: JsonValue | undefined): void => {
    setJsonText(undefined)
    setDoc((current) => {
      if (value === undefined) {
        const { [name]: _removed, ...rest } = current
        return rest
      }
      return { ...current, [name]: value }
    })
  }

  /** A curated field's value as text. Non-strings are shown, not hidden. */
  const text = (name: string): string => {
    const value = doc[name]
    if (value === undefined) return ''
    if (typeof value === 'string') return value
    return typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : displayValue(value)
  }

  const setText = (name: string, value: string): void => {
    setVar(name, value === '' ? undefined : value)
  }

  /** A block's current contents, or an empty one when it is absent or not an object. */
  const block = (name: string): Readonly<Record<string, JsonValue>> => {
    const value = doc[name]
    return isJsonObject(value) ? value : {}
  }

  const blockText = (name: string, key: string): string => {
    const value = block(name)[key]
    if (value === undefined) return ''
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : displayValue(value)
  }

  /*
   * Writes one key of a block, merging rather than rebuilding.
   *
   * The distinction is the whole story in miniature: rebuilding `customer_info`
   * from the four controls would drop a fifth key somebody had set in the JSON
   * view, which is the failure this design exists to prevent one level up.
   */
  const setInBlock = (name: string, key: string, value: JsonValue | undefined): void => {
    setJsonText(undefined)
    setDoc((current) => {
      const existingBlock = isJsonObject(current[name]) ? current[name] : {}
      const next: Record<string, JsonValue> = { ...existingBlock }
      if (value === undefined) delete next[key]
      else next[key] = value
      return { ...current, [name]: next }
    })
  }

  /** A number control's write. Blank removes; anything unusable is left alone. */
  const setNumberInBlock = (name: string, key: string, raw: string): void => {
    if (raw === '') {
      setInBlock(name, key, undefined)
      return
    }
    const value = Number(raw)
    if (Number.isFinite(value)) setInBlock(name, key, value)
  }

  const backend = searchBackendOf(doc)
  /*
   * The block that was swapped out, so switching back does not lose a size
   * somebody chose. Only one of the two is ever in the document — the API
   * refuses both, and sending both asks the installer for two search backends.
   */
  const stashedBlock = useRef<Partial<Record<SearchBackendChoice, JsonValue>>>({})

  const chooseBackend = (choice: SearchBackendChoice): void => {
    if (choice === backend) return
    setJsonText(undefined)
    setDoc((current) => {
      const { opensearch_config: os, es_config: es, ...rest } = current
      if (os !== undefined) stashedBlock.current.opensearch = os
      if (es !== undefined) stashedBlock.current.es = es
      return choice === 'opensearch'
        ? {
            ...rest,
            opensearch_config: stashedBlock.current.opensearch ?? { ...OPENSEARCH_DEFAULT },
          }
        : { ...rest, es_config: stashedBlock.current.es ?? { ...ES_DEFAULT } }
    })
  }

  /** The keys with no control of their own, in the order the document holds them. */
  const extras = extraVariableNames(doc, audience)

  const addVariable = (): void => {
    const name = newVar.name.trim()
    if (name === '') return
    if (Object.prototype.hasOwnProperty.call(doc, name)) {
      // Replacing silently would overwrite a value that is on screen a few rows
      // up, which is the one thing an "add" control must not do.
      setAddFault(`${name} is already set. Edit it in its own row, or remove it first.`)
      return
    }
    setAddFault(undefined)
    setVar(name, parseValue(newVar.value))
    setNewVar({ name: '', value: '' })
  }

  /*
   * What the document is, when it is being typed as JSON and does not parse.
   *
   * Refused here rather than sent: the fault's position is in the parser's
   * message and nowhere else, and the API's 400 would carry none of it.
   */
  const jsonFault = jsonText === undefined ? undefined : readDocumentText(jsonText).problem

  const editJson = (value: string): void => {
    setJsonText(value)
    const read = readDocumentText(value)
    // Committed as soon as it parses, so switching views is not a save step —
    // there is no second state to reconcile, which is the point.
    if (read.document !== undefined) setDoc(read.document)
  }

  const showForm = (): void => {
    // Blocked rather than silently discarding the text: a document that does
    // not parse cannot be projected into a form, and switching anyway would
    // throw away whatever was half-typed, which is the one thing this whole
    // arrangement exists to prevent.
    if (jsonFault !== undefined) return
    setJsonText(undefined)
    setView('form')
  }

  const showJson = (): void => {
    setJsonText(undefined)
    setView('json')
  }

  /*
   * What the installer would refuse this run for, decided before dispatching.
   *
   * `missingRequiredVariables` is the same function the contract exposes, so
   * the console refuses exactly what a run would refuse rather than keeping a
   * second opinion — the arrangement `validateRegistration` already uses for
   * the registration form.
   *
   * It matters because the alternative is not a quick error. A run missing a
   * required variable is accepted by the API, dispatched into the customer's
   * account, pulls the image, assumes the deploy role, initialises the backend
   * and *then* dies — about ninety seconds, a container start and a row in
   * their CloudTrail, to learn that a text field was blank.
   */

  /**
   * The form's names for them, so the message points at a field on screen.
   *
   * `customer_info` names four fields rather than itself, and one of them is
   * labelled differently again: its `email` is the admin email above, not a
   * separate box. "Customer details" alone sent a reader looking for a field
   * that does not exist.
   */
  const MISSING_LABEL: Readonly<Record<string, string>> = {
    deployment_id: 'Deployment id',
    registry_key: 'Registry key',
    customer_info: 'the customer first name, last name, company and admin email',
  }

  /**
   * The deployment this id names, if it already exists.
   *
   * Which decides whether a region is a choice at all. **Choosing a region is
   * part of creating a deployment, not part of every run**: a deployment's
   * state lives at `{deployment_id}/state.tfstate` in one region's bucket, so a
   * run pointed somewhere else finds no state and builds a *second* instance
   * while the console still shows one. `regionConflict` refuses that on the API
   * side; this stops the form offering it.
   */
  const deploymentIdValue = text('deployment_id')
  const existing = deployments?.find((d) => d.deploymentId === deploymentIdValue.trim())
  /** Choices only when the account's regions are known and it has some. */
  const canChooseRegion = regions !== undefined && regions.length > 0
  /**
   * What to send, and nothing when the API has the better answer.
   *
   * Omitted for an existing deployment on purpose: the API takes that
   * deployment's own region from its runs. Sending one would be the console
   * holding a second opinion about where a customer's instance lives, and the
   * two disagreeing is the failure this whole story is about.
   */
  const targetRegion =
    existing !== undefined
      ? undefined
      : (region ?? (canChooseRegion ? regions[0] : '')) || undefined
  /**
   * Whether this run's own region hands the runner a registry key (IM-118).
   *
   * Region-specific because the secret is in the dispatcher stack, and that is
   * per region — so the question is asked of the region this run will actually
   * go to, not of the account. For an existing deployment that is its own
   * recorded region, which is also the one the API will use.
   *
   * Unknown reads as "ask": the region may not be recorded, or nobody may have
   * looked yet. Asking unnecessarily costs one paste; not asking when it was
   * needed costs a run that dies ninety seconds into a container.
   */
  const runRegion = existing !== undefined ? existing.region : targetRegion
  const registryKeyFromAccount =
    runRegion !== undefined && (registryFromAccount ?? []).includes(runRegion)

  /*
   * What the installer would refuse this id for, said here rather than there.
   *
   * The same function the API calls, so the refusal arrives before the request
   * with the same wording — `validateRegistration`'s arrangement. Worth having
   * on this side as well as that one: the id is the field someone types last
   * and the failure it causes is four minutes and a container away, inside a
   * plan, named after a resource they did not write.
   */
  const idFault = deploymentIdProblem(doc['deployment_id'])

  /*
   * The four refusals the contract already owns, all against the document.
   *
   * Every one of them used to be handed a freshly-assembled object built from
   * the form's fields, which is the arrangement that cannot see a variable the
   * form has no field for. They read `doc` now — the same object the request
   * carries — so what is checked is what is sent.
   */
  const missing = missingRequiredVariables(doc, { registryKeyFromAccount })
  const backendFault = searchBackendProblem(doc)

  /* Shape only, and only on a typed one — a chosen one came from the account. */
  const regionFault =
    existing !== undefined || canChooseRegion || targetRegion === undefined
      ? undefined
      : regionProblem(targetRegion)

  /*
   * Fill the form from this deployment's last run, once per id.
   *
   * **Announced, not silent.** The standing rule is that nothing is prefilled
   * silently, and it is a good one — a value carried over unnoticed is how a
   * run goes out subtly different from the one that was reviewed. What that
   * rule is actually protecting against is a value appearing without the person
   * knowing, so this says plainly what it filled and from when, and offers to
   * clear it. The deployment id itself is still never prefilled: it is the one
   * that decides *which instance* a run touches, and typing it is the
   * deliberate act that triggers all of this.
   *
   * Filling between a plan and its apply is not carrying values across
   * instances — it is the same instance, and the values are identical by
   * definition, because an apply that changed them would not be applying what
   * was planned.
   */
  useEffect(() => {
    const remembered = existing?.variables
    const id = existing?.deploymentId
    if (remembered === undefined || id === undefined || filledFrom.current === id) return
    filledFrom.current = id
    setFilledFor(id)
    setJsonText(undefined)
    setDoc((current) => {
      /*
       * The remembered document replaces this one rather than merging into it,
       * and that is the change IM-126 makes. A merge would leave keys from the
       * document being replaced — an `opensearch_config` sitting beside the
       * `es_config` the last run actually used, say — so what went out would be
       * neither what was remembered nor what was on screen.
       *
       * It arrives as `unknown` from the API client and is `JsonValue` by
       * construction: it was parsed out of a JSON response, and the API wrote
       * it from a run's own variables.
       */
      const key = current['registry_key']
      return {
        /*
         * Through `forAudience`, because a remembered document is whatever the
         * last run sent — and if staff planned this instance, that includes
         * `staging_instance`. A customer's apply must not inherit a switch
         * they cannot see.
         */
        ...forAudience(remembered as Readonly<Record<string, JsonValue>>, audience),
        // The instance's own id, always, and after the spread so nothing in it
        // can override that. A run recorded before IM-147 carries
        // `deployment_id: ''`, and letting that through blanked the id on the
        // instance's own screen — so the API minted a fresh key, and a destroy
        // ran against an empty state file and reported success while the real
        // instance kept running.
        deployment_id: id,
        // Never remembered — `splitSecretVariables` strips it before the record
        // is written — so a key already typed is kept rather than blanked.
        ...(typeof key === 'string' && key !== '' ? { registry_key: key } : {}),
      }
    })
  }, [existing])

  /*
   * Back to a new run's document, keeping what identifies this one.
   *
   * The id and the registry key are what someone typed to get here, so clearing
   * a fill they did not want should not cost them both of those again.
   */
  const clearRemembered = (): void => {
    filledFrom.current = undefined
    setFilledFor(undefined)
    setJsonText(undefined)
    setRowText({})
    setDoc((current) => {
      const fresh = initialDocument(deploymentId, tenantId, audience)
      const id = current['deployment_id']
      const key = current['registry_key']
      return {
        ...fresh,
        ...(typeof id === 'string' && id !== '' ? { deployment_id: id } : {}),
        ...(typeof key === 'string' && key !== '' ? { registry_key: key } : {}),
      }
    })
  }

  const permitted = COMMANDS.filter(
    (option) => isRole(role) && roleHasCapability(role, option.capability),
  )

  /** Whether this command would wait, for the instance this form is aimed at. */
  const noticeFor = (command: string): ReturnType<typeof approvalNotice> =>
    approvalNotice(approvalFacts, command, {
      isStaff: requesterIsStaff,
      protection: protectionOf(existing),
    })
  const notices = permitted.flatMap((option) => {
    const answer = noticeFor(option.command)
    return answer === undefined ? [] : [{ label: option.label, ...answer }]
  })
  const waiting = notices.filter((entry) => entry.waits)
  // Would have waited, and will not: nobody at the customer can approve (IM-176).
  const goingAhead = notices.filter((entry) => !entry.waits)

  /*
   * The commands this instance's status rules out, and why.
   *
   * Offered-but-disabled rather than hidden, unlike a missing capability: the
   * role *can* do this, just not to this instance now, and a button that
   * vanishes when a status changes reads as the console losing it. The reason
   * is said in words for the same reason the missing fields are.
   */
  const unsuited = permitted.filter(
    (option) => !commandSuitsStatus(option.command, existing?.status),
  )
  const namedIdValue = deploymentIdValue.trim()
  const unsuitedReason =
    unsuited.length === 0
      ? undefined
      : `${unsuited.map((o) => `${o.label} needs ${o.needs ?? 'an instance'}`).join('; ')} — ${
          existing !== undefined
            ? `${existing.deploymentId} is ${existing.status}.`
            : namedIdValue === ''
              ? 'name the instance first.'
              : `nothing has run as ${namedIdValue} yet.`
        }`

  /**
   * Whether the apply on offer would move this instance to a different version.
   *
   * Compared rather than assumed from "there is a version deployed": an apply
   * against a live instance at the version it already runs is a re-apply, and
   * calling *that* an upgrade would be the same lie in the other direction.
   * Blank means the run sends no version at all, which is also not an upgrade.
   */
  const wanted = text('qrvey_chart_version').trim()
  const upgradeTo =
    deployedVersion !== undefined && wanted !== '' && wanted !== deployedVersion
      ? wanted
      : undefined

  /**
   * The installer this run will use, and whether the chart typed goes with it
   * (IM-196).
   *
   * Offered by a button and never prefilled: IM-107 keeps the chart field empty
   * so the default is never a silent re-apply or upgrade, and a prefill of the
   * installer's version would be exactly that whenever the two differ from the
   * deployed chart.
   */
  const installerImage = runRegion === undefined ? undefined : installerImages?.[runRegion]
  const installerVersion = installerImage === undefined ? undefined : installerTag(installerImage)
  const installerMessage =
    wanted === '' || installerImages === undefined || runRegion === undefined
      ? undefined
      : installerMatchMessage(wanted, chartInstallerMatch(wanted, installerImage), runRegion)

  /*
   * A variable that would route around the role assumed in the container
   * (IM-125), refused before the request with the API's own wording.
   *
   * No longer dormant, which is why IM-125 had to land first: the two views
   * below are both text boxes for an arbitrary variable name, so a text box for
   * `aws_secret_access_key` is now one keystroke away rather than a `curl`
   * away.
   */
  const refusedFault = refusedVariableProblem(doc)

  /*
   * The preflight (IM-36): what the account says about this run, from the API.
   *
   * **One command is asked about**, and it is the apply when the role may
   * start one: the account checks — grant, dispatcher, region — are the same
   * for every command, and the apply is the one that also carries the account
   * block and the budget, which are about creating an instance. Asking once per
   * button would be four cross-account reads for one answer.
   *
   * **It is asked again only when the answer could change**: the region, and
   * whether the id names an instance that exists. Not on every keystroke of
   * the document — every document check is computed above, on this side, with
   * the functions the API uses, and those are the ones `shownChecks` leaves
   * out.
   *
   * **The first answer holds the buttons; later ones do not.** Opening the form
   * and finding Apply live before the account has been asked is the thing the
   * story exists to stop. But a re-check after a change must not freeze a form
   * someone is working in, and an answer for a region they have since changed
   * away from is not an answer about this run — so it is shown as re-checking
   * and gates nothing. A failed request gates nothing either: unknown is not
   * blocking, and the account itself remains the control.
   */
  const preflightCommand =
    permitted.find((option) => option.command === 'apply')?.command ?? permitted[0]?.command
  const preflightKey = JSON.stringify([
    preflightCommand ?? null,
    runRegion ?? null,
    existing?.deploymentId ?? null,
  ])
  const [checked, setChecked] = useState<
    { readonly key: string; readonly result?: PreflightResult; readonly failed?: true } | undefined
  >(undefined)
  const [checkingKey, setCheckingKey] = useState<string | undefined>(undefined)
  /** The document at the moment of asking, without making every edit re-ask. */
  const docRef = useRef(doc)
  docRef.current = doc
  /*
   * The loader too: a caller passing an inline arrow hands over a new function
   * every render, and watching it would re-ask on every render — each answer
   * causing the render that asks again.
   */
  const preflightRef = useRef(preflight)
  preflightRef.current = preflight
  const regionRef = useRef(targetRegion)
  regionRef.current = targetRegion
  const askedOnce = useRef(false)
  const canPreflight = preflight !== undefined

  useEffect(() => {
    if (!canPreflight || preflightCommand === undefined) return
    let live = true
    const key = preflightKey
    const ask = (): void => {
      const load = preflightRef.current
      if (load === undefined) return
      setCheckingKey(key)
      load(preflightCommand, docRef.current, regionRef.current)
        .then((result) => {
          if (live) setChecked({ key, result })
        })
        .catch(() => {
          if (live) setChecked({ key, failed: true })
        })
        .finally(() => {
          if (live) setCheckingKey((current) => (current === key ? undefined : current))
        })
    }
    if (!askedOnce.current) {
      askedOnce.current = true
      ask()
      return () => {
        live = false
      }
    }
    const timer = setTimeout(ask, PREFLIGHT_DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(timer)
    }
    // The region and the document are read, not watched: the key is what
    // decides whether the answer could have changed.
  }, [canPreflight, preflightCommand, preflightKey])

  const current = checked?.key === preflightKey ? checked : undefined
  /** Held until the first answer arrives, and only then. See the note above. */
  const awaitingFirst = preflight !== undefined && checked === undefined
  const blockingChecks =
    current?.result === undefined ? [] : shownChecks(current.result).filter(isBlockingFailure)
  /** Account checks stop every command; a run check only the one asked about. */
  const blockedBy = (command: RunCommand): readonly string[] =>
    blockingChecks
      .filter((c) => c.basis === 'account' || command === current?.result?.command)
      .map((c) => c.title)

  const run = async (command: RunCommand): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      const started = await startRun(
        baseUrl,
        tenantId,
        accountKey,
        command,
        doc,
        targetRegion,
        // Only when this run creates the instance. Creation is the one moment
        // the person knows the answer; afterwards it is changed on the
        // instance's own screen, where what it currently says is visible.
        existing === undefined ? lifetimeRequestFor(lifetime) : undefined,
      )
      onStarted(started)
    } catch (error: unknown) {
      // The reason given, when one was — see the note above. Both shapes: the
      // run record the customer's account refused, and a refusal at the
      // chokepoint (a blocked account, the platform budget), which has no run
      // record and used to read as "Request failed (403)".
      setProblem(explainFailure(error))
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault()
  }

  if (permitted.length === 0) {
    return <p role="status">Your role does not permit starting runs in this account.</p>
  }

  return (
    <form onSubmit={onSubmit} className="start-run">
      {/* No heading of its own: both call sites open this in a modal whose title
        already names the account or the instance, and two headings a line apart
        saying nearly the same thing is how a dialog starts looking generated. */}
      {/*
        Two views over one document. The switch is not a save step: an edit in
        either is committed to `doc` as it is made, so nothing is pending when
        someone changes their mind about how they would rather type it.
      */}
      <div className="tabs">
        <button
          type="button"
          className="tab"
          aria-current={view === 'form'}
          disabled={jsonFault !== undefined}
          onClick={showForm}
          title={
            jsonFault === undefined
              ? undefined
              : 'The document does not parse, so there is no form to project it into.'
          }
        >
          Form
        </button>
        <button type="button" className="tab" aria-current={view === 'json'} onClick={showJson}>
          JSON
        </button>
      </div>

      <p className="hint">
        These are the run&rsquo;s Terraform variables. The other half of the installer&rsquo;s
        <code>config.json</code> &mdash; the region, the state bucket and the state key &mdash; is
        assembled inside the container and is not editable here: those are how this instance finds
        its own state.
      </p>

      {view === 'form' ? (
        <div className="field">
          <label htmlFor="runDeploymentId">Deployment id</label>
          <input
            id="runDeploymentId"
            value={deploymentIdValue}
            // Read-only when re-running an existing deployment: changing it here
            // would silently act on a different instance than the page names.
            readOnly={deploymentId !== undefined}
            aria-describedby="runDeploymentId-hint"
            onChange={(event) => {
              setText('deployment_id', event.target.value)
            }}
          />
          {deploymentId === undefined ? (
            /*
             * Optional since IM-139, and it was never the installer that wanted
             * it: `variable "deployment_id"` defaults to empty and it generates
             * its own, which it keeps in Terraform state. What needed a value
             * was this side, to name the state file — so this side mints one
             * now instead of making somebody invent it.
             *
             * The hint says "we generate one", not "the installer does"
             * (IM-147). Both were true of a blank field; only one of them is
             * the id that comes back on the next screen, and a person who then
             * runs an apply needs to know the id they see is the one to keep.
             */
            <span id="runDeploymentId-hint" className="hint">
              Optional. Leave it blank and we generate one for this instance, which is the ordinary
              case; set it only to match an instance that already has an id &mdash; a migration, or
              a config.json that names one.
            </span>
          ) : null}
        </div>
      ) : null}

      {/*
        Region. A choice when the id names something new, a fact when it does
        not — see `existing` above. The caption says which of the two it is,
        because the control changing shape as someone types an id is otherwise
        the kind of thing a reader assumes is a glitch.
      */}
      {existing !== undefined ? (
        <div className="field">
          <label htmlFor="runRegion">Region</label>
          <input
            id="runRegion"
            readOnly
            value={existing.region ?? 'Not recorded'}
            aria-describedby="runRegion-hint"
          />
          <span id="runRegion-hint" className="hint">
            {existing.region === undefined
              ? 'This deployment predates regions being recorded, so its runs do not say where it is. It will run where this control plane does.'
              : 'Fixed: this deployment already exists, and its state lives here. A run elsewhere would build a second instance rather than update this one.'}
          </span>
        </div>
      ) : canChooseRegion ? (
        <div className="field">
          <label htmlFor="runRegion">Region</label>
          <select
            id="runRegion"
            value={targetRegion ?? ''}
            onChange={(event) => {
              setRegion(event.target.value)
            }}
            aria-describedby="runRegion-hint"
          >
            {regions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <span id="runRegion-hint" className="hint">
            Where this deployment&rsquo;s state lives, and where its instance is built. Only the
            regions this account is set up in are offered.
          </span>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="runRegion">Region</label>
          <input
            id="runRegion"
            value={region ?? ''}
            placeholder="us-east-1"
            onChange={(event) => {
              setRegion(event.target.value)
            }}
            aria-describedby="runRegion-hint"
          />
          <span id="runRegion-hint" className="hint">
            {regions === undefined
              ? 'The regions this account is set up in could not be read, so type one. Leave it blank to use this control plane’s own region.'
              : 'This account has no region set up. Its onboarding screen has the template that adds one; a run before then has nowhere to keep state.'}
          </span>
          {regionFault === undefined ? null : (
            <span role="alert" className="failure">
              {regionFault}
            </span>
          )}
        </div>
      )}

      {/*
        Offered only for a new deployment. A field on every run would get an
        answer typed by somebody guessing on behalf of a colleague — and the
        instance's own screen is where an existing one is changed.
      */}
      {view === 'form' && existing === undefined ? (
        <LifetimePicker value={lifetime} onChange={setLifetimeChoice} />
      ) : null}

      {filledFor === undefined ? null : (
        <p role="status" className="hint">
          Filled in from {filledFor}&rsquo;s last run. The registry key is never remembered, so it
          still needs typing.{' '}
          <button type="button" className="ghost" onClick={clearRemembered}>
            Clear
          </button>
        </p>
      )}

      {view === 'json' ? (
        <div className="field json-field">
          <label htmlFor="runJson">Variables</label>
          <textarea
            id="runJson"
            className="json-editor"
            spellCheck={false}
            rows={22}
            value={jsonText ?? writeDocumentText(doc)}
            aria-describedby="runJson-hint"
            onChange={(event) => {
              editJson(event.target.value)
            }}
          />
          <span id="runJson-hint" className="hint">
            The whole document. Anything typed here that parses is the run&rsquo;s configuration
            immediately &mdash; switching back to the form shows the same document, including keys
            the form has no control for.
          </span>
          {jsonFault === undefined ? null : (
            <span role="alert" className="failure">
              {jsonFault}
            </span>
          )}
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="runChartVersion">Chart version</label>
            {/* Typed, because there is no version list yet — IM-55. */}
            <input
              id="runChartVersion"
              value={text('qrvey_chart_version')}
              placeholder="9.5.0"
              {...(deployedVersion === undefined
                ? {}
                : { 'aria-describedby': 'runChartVersion-hint' })}
              onChange={(event) => {
                setText('qrvey_chart_version', event.target.value)
              }}
            />
            {deployedVersion === undefined ? null : (
              <span id="runChartVersion-hint" className="hint">
                Running now: {deployedVersion}. Leave this blank to apply without asking for a
                version change.
              </span>
            )}
            {installerVersion === undefined || runRegion === undefined ? null : (
              <span className="hint">
                Installer in {runRegion}: {installerVersion}.
                {wanted === installerVersion ? null : (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="linkish"
                      onClick={() => {
                        setText('qrvey_chart_version', installerVersion)
                      }}
                    >
                      Use this version
                    </button>
                  </>
                )}
              </span>
            )}
            {installerMessage === undefined || installerMessage.level === 'none' ? null : (
              <span
                className={
                  installerMessage.level === 'warning' ? 'installer-match-warning' : 'hint'
                }
              >
                {installerMessage.level === 'warning' ? (
                  <>
                    <span aria-hidden="true">!</span> Warning:{' '}
                  </>
                ) : null}
                {installerMessage.text}
              </span>
            )}
          </div>

          <div className="field">
            <label htmlFor="runAdminEmail">Initial admin email</label>
            <input
              id="runAdminEmail"
              type="email"
              value={text('initial_admin_email')}
              onChange={(event) => {
                setText('initial_admin_email', event.target.value)
                /*
                 * The installer seeds the first admin user from `customer_info`,
                 * so the two stay the same address rather than becoming two
                 * fields to keep in step. Written through here because the
                 * document holds both — the old form wrote one and copied it to
                 * the other at send time, which is invisible until you read the
                 * JSON and find a value nobody typed.
                 */
                setInBlock(
                  'customer_info',
                  'email',
                  event.target.value === '' ? undefined : event.target.value,
                )
              }}
              aria-describedby="runAdminEmail-hint"
            />
            <span id="runAdminEmail-hint" className="hint">
              Also written to <code>customer_info.email</code>, which is what seeds the
              instance&rsquo;s first administrator.
            </span>
          </div>

          <div className="field">
            <label htmlFor="runRegistryUser">Registry user</label>
            <input
              id="runRegistryUser"
              value={text('registry_user')}
              onChange={(event) => {
                setText('registry_user', event.target.value)
              }}
            />
          </div>

          <div className="field">
            <label htmlFor="runRegistryKey">Registry key</label>
            {/* type=password so it is not shoulder-read or captured in a
                screenshot of the form; it still travels to the API as a
                Terraform variable, and is stripped before the run is recorded. */}
            <input
              id="runRegistryKey"
              type="password"
              value={text('registry_key')}
              aria-describedby="runRegistryKey-hint"
              onChange={(event) => {
                setText('registry_key', event.target.value)
              }}
            />
            <span id="runRegistryKey-hint" className="hint">
              {registryKeyFromAccount
                ? // Said rather than left to be inferred from an enabled button:
                  // this is the one field that has had to be retyped for every
                  // run, and someone who has been doing that needs telling they
                  // can stop.
                  'Optional: this region’s dispatcher stack holds these credentials, and the runner reads them itself. A key typed here is used instead.'
                : 'Required: this region’s dispatcher stack does not provide registry credentials, so the run has to carry them.'}
            </span>
          </div>

          {/* `customer_info`. Required by the installer, absent from this form
            until IM-89, and therefore the reason no console-started run could
            succeed. Written key by key, so a fifth field somebody added in the
            JSON view survives an edit to the first four. */}
          <div className="field">
            <label htmlFor="runCustomerFirstName">Customer first name</label>
            <input
              id="runCustomerFirstName"
              value={blockText('customer_info', 'firstname')}
              onChange={(event) => {
                setInBlock('customer_info', 'firstname', event.target.value)
              }}
              aria-describedby="runCustomerFirstName-hint"
            />
            <span id="runCustomerFirstName-hint" className="hint">
              Seeds the instance&rsquo;s first administrator, alongside the admin email above.
            </span>
          </div>

          <div className="field">
            <label htmlFor="runCustomerLastName">Customer last name</label>
            <input
              id="runCustomerLastName"
              value={blockText('customer_info', 'lastname')}
              onChange={(event) => {
                setInBlock('customer_info', 'lastname', event.target.value)
              }}
            />
          </div>

          <div className="field">
            <label htmlFor="runCustomerCompany">Company</label>
            <input
              id="runCustomerCompany"
              value={blockText('customer_info', 'company')}
              onChange={(event) => {
                setInBlock('customer_info', 'company', event.target.value)
              }}
              aria-describedby="runCustomerCompany-hint"
            />
            <span id="runCustomerCompany-hint" className="hint">
              Defaults to the customer id. A label on the deployment, not a name it is keyed by.
            </span>
          </div>

          {/*
            Staff only (IM-175). Not rendered for a customer, whose document
            carries no `staging_instance` at all — the installer's default is off.
          */}
          {audience.customer ? null : (
            <div className="field">
              <label htmlFor="runStagingInstance">
                <input
                  id="runStagingInstance"
                  type="checkbox"
                  checked={doc['staging_instance'] === true}
                  onChange={(event) => {
                    setVar('staging_instance', event.target.checked)
                  }}
                  aria-describedby="runStagingInstance-hint"
                />{' '}
                Qrvey-internal instance
              </label>
              <span id="runStagingInstance-hint" className="hint">
                Pulls the chart and images from the dev registry. Correct for Qrvey&rsquo;s own test
                accounts, whose registry tokens are scoped to it; wrong for a customer.{' '}
                {stagingInstanceDefault(environment)
                  ? 'On by default in this Qrvey test environment — untick it for an instance that pulls from the customer registry.'
                  : 'Off by default in this environment — tick it only for one of Qrvey’s own test instances.'}
              </span>
            </div>
          )}

          <div>
            <label htmlFor="runSearchBackend">Search backend</label>
            <select
              id="runSearchBackend"
              value={backend}
              onChange={(event) => {
                chooseBackend(event.target.value as SearchBackendChoice)
              }}
            >
              <option value="opensearch">AWS OpenSearch (managed service)</option>
              <option value="es">Elasticsearch (in the cluster)</option>
            </select>
          </div>

          {backend === 'opensearch' ? (
            <>
              <div>
                <label htmlFor="runOsInstanceType">OpenSearch instance type</label>
                <input
                  id="runOsInstanceType"
                  value={blockText('opensearch_config', 'instance_type')}
                  onChange={(event) => {
                    setInBlock('opensearch_config', 'instance_type', event.target.value)
                  }}
                />
              </div>
              <div>
                <label htmlFor="runOsInstanceCount">OpenSearch instance count</label>
                {/* Written as a number, not a string that is converted at send
                    time: `instance_count` is a number in the document from the
                    moment the form loads, and stays one through the JSON view
                    and back. */}
                <input
                  id="runOsInstanceCount"
                  type="number"
                  min="1"
                  value={blockText('opensearch_config', 'instance_count')}
                  onChange={(event) => {
                    setNumberInBlock('opensearch_config', 'instance_count', event.target.value)
                  }}
                />
              </div>
              <div>
                <label htmlFor="runOsVolumeSize">OpenSearch volume size (GiB)</label>
                <input
                  id="runOsVolumeSize"
                  type="number"
                  min="10"
                  value={blockText('opensearch_config', 'volume_size')}
                  onChange={(event) => {
                    setNumberInBlock('opensearch_config', 'volume_size', event.target.value)
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="runEsSize">Elasticsearch size</label>
                {/* A chart size name such as `small`, not an AWS instance class
                    — the two were confused in this contract for a long time. */}
                <input
                  id="runEsSize"
                  value={blockText('es_config', 'size')}
                  onChange={(event) => {
                    setInBlock('es_config', 'size', event.target.value)
                  }}
                />
              </div>
              <div>
                <label htmlFor="runEsCount">Elasticsearch node count</label>
                <input
                  id="runEsCount"
                  type="number"
                  min="1"
                  value={blockText('es_config', 'count')}
                  onChange={(event) => {
                    setNumberInBlock('es_config', 'count', event.target.value)
                  }}
                />
              </div>
              <div>
                <label htmlFor="runEsStorage">Elasticsearch storage</label>
                {/* A Kubernetes quantity such as `50Gi`. */}
                <input
                  id="runEsStorage"
                  value={blockText('es_config', 'storage')}
                  onChange={(event) => {
                    setInBlock('es_config', 'storage', event.target.value)
                  }}
                />
              </div>
            </>
          )}

          {/*
            Every other key the document carries, in its own order.

            Not behind an "advanced" disclosure, deliberately: a variable
            somebody set is a variable that changes the deployment, and hiding
            it is how a run goes out different from the one that was reviewed.
            IM-62 is what turns these into typed controls with the image's own
            descriptions; until then a name, a value and its type is what can
            honestly be shown.
          */}
          <fieldset className="extras">
            <legend>Other variables</legend>
            {extras.length === 0 ? (
              <p className="hint">
                None. Anything the installer accepts can be added here, and will be offered again on
                the next run against this deployment.
              </p>
            ) : (
              extras.map((name) => (
                <div className="field-row extra-var" key={name}>
                  <div className="field">
                    <label htmlFor={`runExtra-${name}`}>{name}</label>
                    <input
                      id={`runExtra-${name}`}
                      className="var-value"
                      value={rowText[name] ?? displayValue(doc[name] ?? null)}
                      onChange={(event) => {
                        setRowText({ ...rowText, [name]: event.target.value })
                        setVar(name, parseValue(event.target.value))
                      }}
                      onBlur={() => {
                        // Drop the raw text so the committed value renders
                        // canonically — a string typed bare comes back quoted,
                        // which is how someone sees what type they got.
                        const { [name]: _dropped, ...rest } = rowText
                        setRowText(rest)
                      }}
                    />
                  </div>
                  <span className="var-type">{typeLabel(doc[name] ?? null)}</span>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      setVar(name, undefined)
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}

            <div className="field-row">
              <div className="field">
                <label htmlFor="runNewVarName">Add a variable</label>
                <input
                  id="runNewVarName"
                  className="var-value"
                  placeholder="variable_name"
                  value={newVar.name}
                  onChange={(event) => {
                    setNewVar({ ...newVar, name: event.target.value })
                  }}
                />
              </div>
              <div className="field">
                <label htmlFor="runNewVarValue">Value</label>
                <input
                  id="runNewVarValue"
                  className="var-value"
                  placeholder='"text", 100, true, {…}'
                  value={newVar.value}
                  onChange={(event) => {
                    setNewVar({ ...newVar, value: event.target.value })
                  }}
                />
              </div>
              <button type="button" className="ghost" onClick={addVariable}>
                Add
              </button>
            </div>
            <p className="hint">
              Values are read as JSON, so <code>100</code> is a number and <code>true</code> a
              boolean. Anything that is not valid JSON is taken as text, and the type is shown
              beside each row.
            </p>
            {addFault === undefined ? null : (
              <p role="alert" className="failure">
                {addFault}
              </p>
            )}
          </fieldset>
        </>
      )}

      {/* Named rather than left as greyed-out buttons: a disabled control with
        no reason is indistinguishable from a broken one. */}
      {missing.length === 0 ? null : (
        <p className="hint">
          Fill in {missing.map((name) => MISSING_LABEL[name] ?? name).join(', ')} before starting a
          run &mdash; the installer refuses without {missing.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      {idFault === undefined ? null : (
        <p role="alert" className="failure">
          {idFault}
        </p>
      )}

      {refusedFault === undefined ? null : (
        <p role="alert" className="failure">
          {refusedFault}
        </p>
      )}

      {backendFault === undefined ? null : (
        <p role="alert" className="failure">
          {backendFault}
        </p>
      )}

      {unsuitedReason === undefined ? null : <p className="hint">{unsuitedReason}</p>}

      {/* Said before the click, not discovered after it (IM-102, IM-39): a run
        that waits dispatches nothing until somebody agrees. */}
      {waiting.length === 0 ? null : (
        <p className="hint approval-notice">
          {waiting.map(({ label, notice }) => `${label} ${notice}`).join('; ')} — it is recorded now
          and starts only once that is given.
          {waiting
            .flatMap(({ why }) => (why === undefined ? [] : [why]))
            .map((why) => (
              <span key={why} className="approval-why">
                {why}
              </span>
            ))}
        </p>
      )}
      {goingAhead.length === 0 ? null : (
        <p className="hint approval-notice">
          {goingAhead.map(({ label, notice }) => `${label} ${notice}`).join('; ')}. It is recorded
          that nobody could be asked.
        </p>
      )}

      {preflight === undefined ? null : (
        <Preflight
          result={current?.result}
          checking={checkingKey !== undefined}
          failed={current?.failed === true}
          {...(onOpen === undefined ? {} : { onboarding: { tenantId, accountKey, role, onOpen } })}
        />
      )}

      {/* The reason in words, beside the buttons it disables — a greyed-out
        Apply with the explanation three panels up reads as a broken button. */}
      {blockingChecks.length === 0 ? null : (
        <p role="alert" className="failure">
          {preflightReason(
            blockingChecks,
            permitted.find((option) => option.command === current?.result?.command)?.label,
          )}
        </p>
      )}

      <div className="actions">
        {permitted.map((option) => (
          <button
            key={option.command}
            type="button"
            className={option.tone}
            {...(option.command === 'destroy' ? { ref: destroyButton } : {})}
            disabled={
              busy ||
              missing.length > 0 ||
              regionFault !== undefined ||
              idFault !== undefined ||
              refusedFault !== undefined ||
              backendFault !== undefined ||
              jsonFault !== undefined ||
              awaitingFirst ||
              blockedBy(option.command).length > 0 ||
              unsuited.includes(option)
            }
            title={
              option.command === 'apply' && upgradeTo !== undefined ? UPGRADE_HINT : option.hint
            }
            onClick={() => {
              // Destroy asks first. Everything else goes straight out — see
              // `confirm-destroy.tsx` for why only this one.
              if (option.command === 'destroy') {
                setConfirming(true)
                return
              }
              void run(option.command)
            }}
          >
            {option.command === 'apply' && upgradeTo !== undefined
              ? `Upgrade to ${upgradeTo}`
              : option.command === 'destroy' && noticeFor('destroy')?.waits === true
                ? // The one irreversible command says it will ask, on the button.
                  'Request destroy'
                : option.label}
          </button>
        ))}
      </div>

      {confirming ? (
        <ConfirmDestroy
          tenantId={tenantId}
          accountKey={accountKey}
          deploymentId={deploymentIdValue.trim()}
          onConfirm={() => {
            setConfirming(false)
            void run('destroy')
          }}
          onCancel={() => {
            returnFocus.current = true
            setConfirming(false)
          }}
        />
      ) : null}

      {busy ? <p role="status">Starting…</p> : null}
      {problem === undefined ? null : <p role="alert">{problem}</p>}
    </form>
  )
}
